// Inline-SVG trend chart: two categorical series (views, uniques) over time.
// No chart library. SSR-safe: geometry is computed from data + a fixed viewBox
// (scaled to 100% width), never from DOM measurement, and dates are formatted
// deterministically so server and client markup match. Colors are the
// dataviz-validated --color-chart-1 / --color-chart-2 tokens.
import * as React from "react";

type TrendPoint = { date: string; views: number; uniques: number };

const SERIES = [
  { key: "views", label: "Views", color: "var(--color-chart-1)" },
  { key: "uniques", label: "Unique visitors", color: "var(--color-chart-2)" },
] as const;

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function fmtDay(iso: string) {
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
}

function niceCeil(v: number) {
  if (v <= 5) return 5;
  const pow = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

export function TrendChart({ data }: { data: TrendPoint[] }) {
  const [hover, setHover] = React.useState<number | null>(null);

  const W = 720;
  const H = 260;
  const pad = { top: 16, right: 18, bottom: 30, left: 40 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const n = data.length;
  const hasData = data.some((d) => d.views > 0 || d.uniques > 0);
  const yMax = niceCeil(Math.max(1, ...data.map((d) => Math.max(d.views, d.uniques))));

  const x = (i: number) =>
    pad.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => pad.top + plotH - (v / yMax) * plotH;

  const line = (key: "views" | "uniques") =>
    data.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d[key])}`).join(" ");
  const area = (key: "views" | "uniques") =>
    `${line(key)} L ${x(n - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`;

  const gridVals = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const ticks = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

  if (!hasData) {
    return (
      <div className="flex h-48 items-center justify-center rounded-lg border border-hairline bg-page">
        <p className="text-sm text-ink-muted">No visits recorded in this range yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-hairline bg-page p-4">
      <div className="mb-2 flex items-center gap-4">
        {SERIES.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-ink-muted">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: s.color }}
            />
            {s.label}
          </span>
        ))}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label="Daily views and unique visitors over the selected range"
        onMouseLeave={() => setHover(null)}
      >
        {/* gridlines + y labels */}
        {gridVals.map((v) => (
          <g key={v}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={y(v)}
              y2={y(v)}
              stroke="var(--color-hairline)"
              strokeWidth={1}
            />
            <text
              x={pad.left - 6}
              y={y(v)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill="var(--color-ink-muted)"
            >
              {v}
            </text>
          </g>
        ))}

        {/* x labels */}
        {ticks.map((i) => (
          <text
            key={i}
            x={x(i)}
            y={H - 8}
            textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}
            fontSize={11}
            fill="var(--color-ink-muted)"
          >
            {fmtDay(data[i].date)}
          </text>
        ))}

        {/* views area + line */}
        <path d={area("views")} fill="var(--color-chart-1)" opacity={0.12} />
        <path
          d={line("views")}
          fill="none"
          stroke="var(--color-chart-1)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* uniques line */}
        <path
          d={line("uniques")}
          fill="none"
          stroke="var(--color-chart-2)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* hover crosshair + emphasized points */}
        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={pad.top}
            y2={pad.top + plotH}
            stroke="var(--color-ink-muted)"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}
        {hover !== null &&
          SERIES.map((s) => (
            <circle
              key={s.key}
              cx={x(hover)}
              cy={y(data[hover][s.key])}
              r={4}
              fill="var(--color-page)"
              stroke={s.color}
              strokeWidth={2}
            />
          ))}

        {/* per-day hit targets */}
        {data.map((d, i) => {
          const bandLeft = i === 0 ? pad.left : (x(i - 1) + x(i)) / 2;
          const bandRight = i === n - 1 ? W - pad.right : (x(i) + x(i + 1)) / 2;
          return (
            <rect
              key={d.date}
              x={bandLeft}
              y={pad.top}
              width={Math.max(bandRight - bandLeft, 1)}
              height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
            />
          );
        })}

        {/* tooltip */}
        {hover !== null && <Tooltip i={hover} data={data} x={x} W={W} pad={pad} />}
      </svg>
    </div>
  );
}

function Tooltip({
  i,
  data,
  x,
  W,
  pad,
}: {
  i: number;
  data: TrendPoint[];
  x: (i: number) => number;
  W: number;
  pad: { top: number; right: number; left: number };
}) {
  const bw = 150;
  const bh = 62;
  const cx = x(i);
  const bx = Math.min(Math.max(cx + 10, pad.left), W - pad.right - bw);
  const by = pad.top + 4;
  const d = data[i];

  return (
    <g pointerEvents="none">
      <rect
        x={bx}
        y={by}
        width={bw}
        height={bh}
        rx={6}
        fill="var(--color-page)"
        stroke="var(--color-hairline)"
        strokeWidth={1}
      />
      <text x={bx + 10} y={by + 18} fontSize={11} fill="var(--color-ink-muted)">
        {fmtDay(d.date)}
      </text>
      <text x={bx + 10} y={by + 36} fontSize={12} fill="var(--color-ink-display)">
        <tspan fontWeight={600} fill="var(--color-chart-1)">
          {d.views}
        </tspan>
        <tspan dx={4}>views</tspan>
      </text>
      <text x={bx + 10} y={by + 52} fontSize={12} fill="var(--color-ink-display)">
        <tspan fontWeight={600} fill="var(--color-chart-2)">
          {d.uniques}
        </tspan>
        <tspan dx={4}>unique</tspan>
      </text>
    </g>
  );
}
